import type { OfflineReplayRequest } from '@/types'
import {
  isTerminalReplayError,
  pendingOfflineOrders,
  updateOfflineOrder,
  type OfflineOrder,
} from './offlineStore'

/**
 * Replaying the offline queue on reconnect.
 *
 * The guiding rule: a sale that already happened must never be discarded
 * silently. Only two outcomes end an operation — `synced`, where the
 * order exists server-side, and `unreconciled`, which is not a drop but a
 * handover to a supervised queue where a manager bills or writes it off with an
 * audit trail. Everything else retries.
 */

export type ReplayOutcome = 'synced' | 'retry' | 'unreconciled'

/**
 * Decide what a replay result means for one queued operation.
 *
 * `offline_order_duplicate` is a SUCCESS: it means the first attempt actually
 * landed and the server already holds the order. Treating it as a failure
 * would strand a perfectly good order in the queue forever.
 *
 * Anything unrecognised retries. An unknown error must never discard a sale
 * the customer already walked out with.
 */
export function classifyReplayOutcome(errorCode: string | null): ReplayOutcome {
  if (errorCode == null) return 'synced'
  if (errorCode === 'offline_order_duplicate') return 'synced'
  if (isTerminalReplayError(errorCode)) return 'unreconciled'
  return 'retry'
}

/** The wire shape lives in @/types so the API client and this engine agree. */
export type ReplayRequest = OfflineReplayRequest

export interface ReplayDeps {
  /** Resolves with the stable error code, or null on success. Must not throw. */
  replay: (op: ReplayRequest) => Promise<{ errorCode: string | null }>
  /**
   * Hand a permanently-failed operation to the server-side reconciliation
   * queue. Without this the queue stays empty, Day Close never blocks, and the
   * goods that were sold offline but never posted are invisible to anyone but
   * this terminal — which is the whole failure this feature exists to prevent.
   *
   * Must not throw; a failed report leaves the entry pending so it is retried.
   */
  report: (op: OfflineOrder, failureCode: string) => Promise<boolean>
}

export interface ReplaySummary {
  synced: number
  retry: number
  unreconciled: number
}

/** Shape one queued entry into the wire request. */
export function toReplayRequest(op: OfflineOrder): ReplayRequest {
  return {
    op_id: op.opId,
    order_number: op.orderNumber,
    captured_at: op.capturedAt,
    order_type: op.orderType,
    ...(op.targetOrderId ? { target_order_id: op.targetOrderId } : {}),
    lines: op.lines.map((l) => ({
      product_id: l.productId,
      quantity: l.quantity,
      ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
    })),
  }
}

/**
 * Replay every pending operation in CAPTURE ORDER.
 *
 * Order matters: entries are replayed oldest-first so the day each was taken on
 * is resolved in the sequence it happened, and so a future add-items operation
 * can never overtake the create it depends on.
 *
 * Runs sequentially rather than in parallel. A burst of concurrent replays
 * against a backend that has just come back is exactly when it is least able to
 * absorb one, and the queue is small by construction.
 */
export async function replayOfflineQueue(deps: ReplayDeps): Promise<ReplaySummary> {
  const summary: ReplaySummary = { synced: 0, retry: 0, unreconciled: 0 }

  for (const op of await pendingOfflineOrders()) {
    let errorCode: string | null
    try {
      ;({ errorCode } = await deps.replay(toReplayRequest(op)))
    } catch {
      // A thrown transport error is retryable, never terminal — the outage may
      // simply not be over yet.
      errorCode = 'network_error'
    }

    const outcome = classifyReplayOutcome(errorCode)
    summary[outcome]++

    if (outcome === 'synced') {
      await updateOfflineOrder(op.opId, { status: 'synced', lastError: null })
    } else if (outcome === 'unreconciled') {
      // Only mark it locally resolved once the SERVER has the item. If the
      // report fails, leave the entry pending so the next drain tries again —
      // a local "unreconciled" flag nobody else can see is no better than
      // dropping the food on the floor.
      const reported = await deps.report(op, errorCode ?? 'unknown_error')
      if (reported) {
        await updateOfflineOrder(op.opId, { status: 'unreconciled', lastError: errorCode })
      } else {
        summary.unreconciled--
        summary.retry++
        await updateOfflineOrder(op.opId, { attempts: op.attempts + 1, lastError: errorCode })
      }
    } else {
      await updateOfflineOrder(op.opId, { attempts: op.attempts + 1, lastError: errorCode })
    }
  }

  return summary
}
